/* eslint-disable */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../components/Iconify';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthToken, setAuthUser } from '../../redux/reducers/authSlice';
import { authApiCall } from '../../services/http';
import { endpoints } from '../../config';
import { SUB_STATUS_OPTIONS } from '../../helpers/subscriptions';

// pricePerUserEur y flatPriceEur llegan como string ('9.99') por el DECIMAL de Postgres.
const decimalToNumber = (v) => (v == null || v === '' ? null : Number(v));
// Convierte un input controlado (string) al modelo (number | null).
const inputToNumber = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// 'YYYY-MM-DD' ↔ ISO. El input nativo type="date" sólo expone la parte fecha
// (en hora local del cliente), pero la sub guarda timestamptz en BD (p.ej.
// '2026-05-15 19:14:49.000 +0200'). Al volver a ISO conservamos la hora del valor
// original para no pisar la parte de tiempo; sólo hay fallback (mediodía UTC) si
// no había valor previo.
const isoToYMD = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const buildPeriodEnd = (ymd, originalIso) => {
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (originalIso) {
    const orig = new Date(originalIso);
    if (!Number.isNaN(orig.getTime())) {
      const next = new Date(orig);
      // Cambiamos sólo la fecha (en hora local, igual que la lee el input) y dejamos
      // intacta la hora original. Usamos setFullYear con month y date juntos para
      // evitar saltos cuando la fecha previa cae a fin de mes.
      next.setFullYear(y, m - 1, d);
      return next.toISOString();
    }
  }
  return new Date(`${ymd}T12:00:00.000Z`).toISOString();
};

const BILLING_INTERVAL_OPTIONS = [
  { value: '', label: '— sin definir —' },
  { value: 'month', label: 'Mensual' },
  { value: 'year', label: 'Anual' },
  { value: 'one_time', label: 'Pago único' },
];

SubscriptionDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscription: PropTypes.object,
  onSaved: PropTypes.func, // (updatedSub) => void
};

export default function SubscriptionDetailsDialog({ open, onClose, subscription, onSaved }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.authToken);
  const fullScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const [maxSeats, setMaxSeats] = useState('');
  const [pricePerUserEur, setPricePerUserEur] = useState('');
  const [flatPriceEur, setFlatPriceEur] = useState('');
  const [priceMode, setPriceMode] = useState('perUser'); // 'perUser' | 'flat'
  const [status, setStatus] = useState('');
  const [billingInterval, setBillingInterval] = useState('');
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState('');
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // currentPeriodEnd solo es editable en subs manuales (el backend rechaza con 400 si hay stripeSubscriptionId).
  const isManual = !subscription?.stripeSubscriptionId;

  // Reset al abrir o cuando cambia la sub
  useEffect(() => {
    if (!open || !subscription) return;
    const i = {
      maxSeats: subscription.maxSeats ?? null,
      pricePerUserEur: decimalToNumber(subscription.pricePerUserEur),
      flatPriceEur: decimalToNumber(subscription.flatPriceEur),
      status: subscription.status ?? null,
      billingInterval: subscription.billingInterval ?? null,
      currentPeriodEnd: subscription.currentPeriodEnd ?? null,
    };
    setInitial(i);
    setMaxSeats(i.maxSeats == null ? '' : String(i.maxSeats));
    setPricePerUserEur(i.pricePerUserEur == null ? '' : String(i.pricePerUserEur));
    setFlatPriceEur(i.flatPriceEur == null ? '' : String(i.flatPriceEur));
    setPriceMode(i.flatPriceEur != null ? 'flat' : 'perUser');
    setStatus(i.status || '');
    setBillingInterval(i.billingInterval || '');
    setCurrentPeriodEnd(isoToYMD(i.currentPeriodEnd));
    setError(null);
  }, [open, subscription]);

  if (!subscription) return null;

  const currentPricePerUser = inputToNumber(pricePerUserEur);
  const currentFlat = inputToNumber(flatPriceEur);
  const pricePerUserDisabled = priceMode !== 'perUser';
  const flatPriceDisabled = priceMode !== 'flat';

  const switchToFlat = () => {
    setPricePerUserEur('');
    setPriceMode('flat');
  };
  const switchToPerUser = () => {
    setFlatPriceEur('');
    setPriceMode('perUser');
  };

  const save = async () => {
    if (!initial) return;
    setSaving(true);
    setError(null);

    const form = {
      maxSeats: inputToNumber(maxSeats),
      pricePerUserEur: currentPricePerUser,
      flatPriceEur: currentFlat,
    };

    const body = {};
    if (form.maxSeats !== initial.maxSeats) body.maxSeats = form.maxSeats;

    // Status: '' tratado como null (sin cambios) sólo si el inicial era null.
    const nextStatus = status || null;
    if (nextStatus !== (initial.status ?? null)) body.status = nextStatus;

    const nextInterval = billingInterval || null;
    if (nextInterval !== (initial.billingInterval ?? null)) body.billingInterval = nextInterval;

    // currentPeriodEnd solo se manda en subs manuales (Stripe → 400).
    if (isManual) {
      const initialYMD = isoToYMD(initial.currentPeriodEnd);
      if (currentPeriodEnd !== initialYMD) {
        body.currentPeriodEnd = currentPeriodEnd ? buildPeriodEnd(currentPeriodEnd, initial.currentPeriodEnd) : null;
      }
    }

    // Mutex precio (lógica del spec): si ahora hay pricePerUser, mandar flat a null y viceversa.
    if (form.pricePerUserEur !== null) {
      if (form.pricePerUserEur !== initial.pricePerUserEur) body.pricePerUserEur = form.pricePerUserEur;
      if (initial.flatPriceEur !== null) body.flatPriceEur = null;
    } else if (form.flatPriceEur !== null) {
      if (form.flatPriceEur !== initial.flatPriceEur) body.flatPriceEur = form.flatPriceEur;
      if (initial.pricePerUserEur !== null) body.pricePerUserEur = null;
    } else {
      if (initial.pricePerUserEur !== null) body.pricePerUserEur = null;
      if (initial.flatPriceEur !== null) body.flatPriceEur = null;
    }

    if (Object.keys(body).length === 0) {
      setSaving(false);
      onClose();
      return;
    }

    try {
      const res = await authApiCall({
        method: 'PUT',
        endpoint: endpoints.subscriptions,
        path: `/${subscription.id}`,
        data: body,
        navigate,
        dispatch,
        setAuthUser,
        setAuthToken,
        authToken,
        redirectRoute: null,
      });
      // Merge defensiva: garantizamos que la fila siempre se actualiza con los cambios
      // aunque el backend responda con un payload parcial (o sin id).
      if (onSaved) onSaved({ ...subscription, ...body, ...(res?.data || {}) });
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Detalles de la suscripción</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Estado" value={status} onChange={(e) => setStatus(e.target.value)} fullWidth>
              {/* Si la sub viene con un status fuera del whitelist (p.ej. past_due), lo incluimos para no perderlo. */}
              {!SUB_STATUS_OPTIONS.map((s) => s.value).includes(status) && status && (
                <MenuItem value={status}>{status}</MenuItem>
              )}
              {SUB_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Fin de periodo"
              type="date"
              value={currentPeriodEnd}
              onChange={(e) => setCurrentPeriodEnd(e.target.value)}
              fullWidth
              disabled={!isManual}
              InputLabelProps={{ shrink: true }}
              helperText={isManual ? 'Solo subs manuales' : 'Lo gestiona Stripe'}
            />
          </Stack>

          <TextField
            select
            label="Facturación"
            value={billingInterval}
            onChange={(e) => setBillingInterval(e.target.value)}
            fullWidth
            helperText={
              isManual
                ? 'Cómo se cobra esta sub manual.'
                : 'Lo deriva Stripe; cambiarlo aquí no afecta al producto en Stripe.'
            }
          >
            {BILLING_INTERVAL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || 'none'} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Asientos (maxSeats)"
            type="number"
            value={maxSeats}
            onChange={(e) => setMaxSeats(e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
            sx={{
              // Oculta los spinners nativos del navegador para que sólo se vean los de abajo.
              '& input[type=number]': { MozAppearance: 'textfield' },
              '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
              '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const n = Number(maxSeats);
                      if (Number.isFinite(n) && n > 0) setMaxSeats(String(n - 1));
                    }}
                    disabled={!maxSeats || Number(maxSeats) <= 0}
                  >
                    <Iconify icon="eva:minus-outline" width={18} height={18} />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const n = Number(maxSeats);
                      setMaxSeats(String((Number.isFinite(n) ? n : 0) + 1));
                    }}
                  >
                    <Iconify icon="eva:plus-outline" width={18} height={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            helperText="Vacío = sin límite (null)"
          />

          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label="Precio por usuario (EUR)"
                type="number"
                value={pricePerUserEur}
                onChange={(e) => setPricePerUserEur(e.target.value)}
                disabled={pricePerUserDisabled}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
              {pricePerUserDisabled && (
                <Button onClick={switchToPerUser} size="small">
                  Usar
                </Button>
              )}
            </Stack>
          </Box>

          <Box>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                label="Precio cerrado (EUR)"
                type="number"
                value={flatPriceEur}
                onChange={(e) => setFlatPriceEur(e.target.value)}
                disabled={flatPriceDisabled}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
              />
              {flatPriceDisabled && (
                <Button onClick={switchToFlat} size="small">
                  Usar
                </Button>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Solo uno de los dos. Vaciar ambos = sin precio configurado.
            </Typography>
          </Box>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <LoadingButton onClick={save} variant="contained" loading={saving}>
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
